import {
  createSignal,
  createContext,
  useContext,
  createEffect,
  createDeferred,
  createMemo,
  on,
  onCleanup,
  createResource,
  createRoot,
} from 'solid-js';
import { createStore, produce, unwrap } from 'solid-js/store';
import { Connector, Dataset } from '@oceanum/datamesh';
import {
  Transform,
  useTimer,
  DataWrapper,
  spec,
  EventBusContext,
  usesEventInput,
} from 'framework';
import {
  removeTransform,
  replaceTransform,
  terminateAll,
} from './transform-registry';
import { createEventTransformProxyCache } from './event-transform-proxy-cache';
import {
  diffSnapshotEntries,
  pruneByIds,
  sameSnapshotEntries,
  sourceStillPresent,
} from './snapshot-diff';
import {
  readOwnInputs,
  sameInputIdentities,
} from './transform-input-signature';
// EID-125: inputs are per-input Datamesh query specs ({ datasource, ...query
// overrides }); normalization shims legacy string entries and fail-loud
// validates forms the client engine cannot execute yet.
import { normalizeTransformInputs } from './transform-input-query';
// EID-126: code-only spec changes take a warm-recompile fast path — the diff
// classifier tells them apart from structural changes, the code proxy gives
// the recompiled transform a fresh registry identity (consumer query caches
// key on source identity), and the status module types the per-transform
// lifecycle published alongside the registry.
import { classifyTransformSpecChange } from './transform-spec-diff';
import { createTransformCodeProxy } from './transform-code-proxy';
import {
  errorStatus,
  initializingStatus,
  type TransformStatus,
} from './transform-status';
// EID-132: PMTiles archives are read as tiles, not loaded as a Dataset — the
// datasource entry carries a tile-access handle (see ./pmtiles.ts).
import { openPMTiles, pmtilesRequestHeaders } from './pmtiles';
// EID-131: GeoZarr multiscale conventions — level descriptors, the attribute
// normalization table, and the openScale surface a future LOD scheduler calls.
import { fetchConsolidatedMetadata, openWithScales } from './multiscale';
// EID-137: the LOD policy turns a camera into a resolution demand; this file
// owns the registry, so it owns the act of swapping a pyramid level.
import { planScaleChange } from './lod';
import { createLogger } from 'utils';

// EID-64: debug-gated so production builds don't log/retain the full data
// registry on every recompute. Enable with `localStorage.debug = 'eidos:data'`.
const log = createLogger('data');

const [_dataLoading, _setDataLoading] = createStore([]);

const useDataLoading = () => {
  const current = createMemo(() => [..._dataLoading]);
  const loading = () => _dataLoading.length > 0;
  const contains = (ids) => {
    if (!ids) return false;
    return _dataLoading.some((id) => ids.includes(id));
  };
  const includes = (id) => {
    if (!id) return false;
    return _dataLoading.includes(id);
  };

  return {
    current,
    loading,
    contains,
    includes,
    start(id) {
      _setDataLoading((p) => (p.includes(id) ? p : [...p, id]));
    },
    stop(id) {
      _setDataLoading((p) => (p.includes(id) ? p.filter((i) => i !== id) : p));
    },
  };
};

const dataLoading = useDataLoading();

const needsRefresh = (dataspec, timer) => {
  if (!dataspec || !dataspec.query) return false;
  const refresh = dataspec.query.refresh || 60;
  return (
    dataspec.query.timefilter &&
    dataspec.query.timefilter.times[1] === null &&
    timer % refresh == 0
  );
};

const DataContext = createContext({});
const useData = () => useContext(DataContext);

// EID-86: only map pointer interactions drive $inputs.$event re-queries. The
// event bus also carries UI events (viewStateChange on every pan/zoom, menu
// changes, panel open/close, status) — gating on those would re-run (and reset)
// event-using transforms on unrelated activity. Action names come from
// map-container onClick.
const MAP_INTERACTION_ACTIONS = new Set(['click', 'doubleClick', 'rightClick']);

const DataProvider = (props) => {
  const [datamesh, setDatamesh] = createSignal([]);
  const [inline, setInline] = createSignal([]);
  const [zarr, setZarr] = createSignal([]);
  const [pmtiles, setPmtiles] = createSignal([]);
  const [data, setData] = createSignal({});
  const [dataSpec, setDataSpec] = createSignal({});
  const [refresh, setRefresh] = createSignal({});
  const [transforms, setTransforms] = createSignal({});
  // EID-126: per-transform lifecycle status (initializing / importing-modules
  // / ready / error{phase}), published through the context alongside the
  // registry so UIs can surface failures that previously only reached the
  // console. Written by the watchers below via the Transform onStatus sink.
  const [transformStatus, setTransformStatus] = createStore<
    Record<string, TransformStatus>
  >({});
  const [datameshGateway, setDatameshGateway] = createSignal(null);
  // EID-86: the latest map interaction event, mirrored from the event bus when
  // an event-using transform exists (see the gated effect below). dataRegistry
  // depends on it so a new event re-drives bound consumers.
  const [currentEvent, setCurrentEvent] = createSignal(null);

  // EID-86: read the bus directly (not useEventBus) so DataProvider stays
  // mountable without an EventBusProvider ancestor — outside it, lastEvent is a
  // constant null accessor and the $event feature is simply inert.
  const eventBus = useContext(EventBusContext) as
    { lastEvent: () => any } | undefined;
  const lastEvent = eventBus?.lastEvent ?? (() => null);
  const { timer } = useTimer();

  const getZarr = async (url, headers, coordkeys, ttl) => {
    // Strip trailing slash to prevent double-slash in zarr paths
    const cleanUrl = url.endsWith('/') ? url.slice(0, -1) : url;
    return await Dataset.zarr(cleanUrl, headers, { coordkeys, ttl });
  };

  // EID-131: open a zarr datasource and, when the store turns out to be a
  // GeoZarr multiscale pyramid, hand back the level-addressable surface
  // alongside the dataset. The flow (and its back-compat guarantees) lives in
  // ./multiscale.ts — openWithScales — so it is testable without the datamesh
  // client; this only supplies the datamesh-backed callbacks.
  //
  // An explicit spec `group` keeps winning unchanged: it IS the url we open.
  const openZarrSource = (url, headers, coordkeys, ttl) => {
    const cleanUrl = url.endsWith('/') ? url.slice(0, -1) : url;
    return openWithScales({
      // No level path means the pre-EID-131 call, character for character.
      // With one, the same open scoped to that level group; per-level cache
      // keys already fall out of CachedHTTPStore's root-URL hashing.
      open: (group?: string) =>
        group === undefined
          ? getZarr(url, headers, coordkeys, ttl)
          : Dataset.zarr(cleanUrl, headers, { coordkeys, ttl, group }),
      // Returns null on any failure (no `.zmetadata`, non-JSON body, or a 401
      // from a gateway endpoint whose auth travels as query params rather than
      // headers), which simply means "not a describable pyramid".
      loadMetadata: () => fetchConsolidatedMetadata(cleanUrl, headers),
      onFallback: (level, error) =>
        console.warn(
          `Zarr store root is a multiscale pyramid and cannot be opened ` +
            `whole (${(error as Error)?.message ?? error}); opened level ` +
            `${level.level} instead.`,
        ),
    });
  };

  const getData = async (query, maxage, resptype = null) => {
    return await datameshGateway().query(unwrap(query), { timeout: 120000 });
  };

  const stageData = async (query) => {
    return await datameshGateway().stageRequest(unwrap(query));
  };

  // Load a data source's schema (dimensions + variables) into a data-less
  // dataset by opening the staged query's lazy zarr endpoint (.zmetadata only,
  // no chunks). Works for any size, so editor functions know which sources a
  // node type can bind to — including large sources we never download.
  // Minimal shape of a staged Datamesh request we read here — types the two
  // fields getSchema touches instead of casting the stage to `any`.
  interface StagedRequest {
    zarr_endpoint?: string;
    coordkeys?: Record<string, string>;
  }
  const getSchema = async (stage: StagedRequest, query) => {
    const zarrPath = stage?.zarr_endpoint;
    if (!zarrPath) return null;
    const gateway = datameshGateway();
    if (!gateway) return null;
    const url = `${props.gatewayService}${zarrPath}`;
    // Open through the connector so the datamesh session header
    // (X-DATAMESH-SESSIONID) is attached — the gateway 401s "Session ID
    // required" on a /zarr/query request without it. This path used to
    // hand-roll a bearer-only header set and call Dataset.zarr directly,
    // which broke schema loads for geofilter/zarr-transported sources once
    // the gateway made v1 sessions mandatory.
    const dataset = await gateway.openZarr(url, {
      coordkeys: stage.coordkeys,
    });
    // Narrow to the requested variables, mirroring connector.query().
    if (query?.variables) {
      for (const v of Object.keys(dataset.variables)) {
        if (
          !query.variables.includes(v) &&
          !Object.values(dataset.coordkeys).includes(v)
        ) {
          delete dataset.variables[v];
        }
      }
    }
    return dataset;
  };

  // Event-using transforms must re-run whenever the interaction event changes.
  // BaseWorldLayer.cachedQuery dedupes by (query, source identity), and a map
  // click changes neither — so we hand consumers a fresh wrapper *identity* per
  // event (keyed on the current event), which busts that cache and re-runs the
  // transform with the new $inputs.$event. The wrapper delegates to the live
  // Transform so datasource/refresh stay current. Non-event transforms pass
  // through unchanged (stable identity → no spurious re-queries). Extracted to
  // ./event-transform-proxy-cache (EID-110 M2/R8) so stopTransformWatcher can
  // evict a retired transform's entry instead of leaking it indefinitely.
  const eventTransformProxies = createEventTransformProxyCache();

  const dataRegistry = createMemo(() => {
    // EID-86: subscribe to the current map event so a new interaction
    // invalidates the registry, prompting bound consumers (panels/layers) to
    // re-query — which is when Transform.query injects the fresh $inputs.$event.
    const event = currentEvent();
    // Event-using transforms get a per-event wrapper identity so consumer query
    // caches re-run them on each interaction (see eventTransformProxies above).
    const registryTransforms: Record<string, unknown> = {};
    for (const [id, tx] of Object.entries(transforms())) {
      registryTransforms[id] = (tx as any).usesEvent
        ? eventTransformProxies.get(id, tx as any, event)
        : tx;
    }
    // Lazy: the registry objects are only materialised for the log when the
    // 'eidos:data' namespace is enabled (EID-64).
    log.lazy(() => ['Data registry', data(), transforms()]);
    return {
      ...data(),
      ...registryTransforms,
    };
  });

  createEffect(() => {
    log('updated ', timer());
  });

  createEffect(
    on([props.authToken], () => {
      if (!props.authToken()) {
        console.warn('Must set Oceanum auth to use Datamesh');
        return;
      }
      setDatameshGateway(
        new Connector(null, {
          jwtAuth: props.authToken(),
          service: props.datameshService,
          gateway: props.gatewayService,
        }),
      );
    }),
  );

  // EID-61: any mutation under spec.data previously re-classified everything
  // with fresh array identities, re-fetching all inline datasets, re-opening
  // all zarr stores and terminate+recreating all transform workers. Each
  // category now gets a memo of per-entry JSON snapshots: the stringify walk
  // reads every nested property of that category's entries through the store
  // proxy (so deep changes — including remote Yjs patches — are still
  // observed), while sameSnapshotEntries stops propagation when the
  // category's content did not actually change. The effects below then diff
  // snapshots per id and only re-process changed entries.
  const dataEntrySnapshot = (d) => ({ id: d.id, snapshot: JSON.stringify(d) });
  const ofType = (...types: string[]) =>
    (spec.data ?? []).filter((d) => types.includes(d.dataType));

  // EID-86: does any transform read the interaction event? Used to gate the
  // event→re-query invalidation below so specs that don't use it pay nothing
  // (map clicks are frequent and the map itself consumes dataRegistry).
  const hasEventTransforms = createMemo(() =>
    ofType('transform').some((t) => usesEventInput(t.dataSpec?.code)),
  );

  // EID-86: mirror a map pointer interaction into currentEvent (only when an
  // event-using transform exists). This invalidates dataRegistry, so bound
  // consumers re-query and Transform.query injects the fresh $inputs.$event. The
  // worker is re-queried, not re-initialised (that watcher depends on data()).
  // Non-pointer bus events (pan/zoom, menu, panels, status) are ignored so they
  // don't reset event-using transforms on unrelated activity.
  createEffect(
    on(
      lastEvent,
      (e) => {
        if (e && MAP_INTERACTION_ACTIONS.has(e.action) && hasEventTransforms())
          setCurrentEvent(e);
      },
      { defer: true },
    ),
  );

  const datameshEntries = createMemo(
    () => ofType('oceanql').map(dataEntrySnapshot),
    undefined,
    { equals: sameSnapshotEntries },
  );
  const inlineEntries = createMemo(
    () => ofType('dataset', 'geojson').map(dataEntrySnapshot),
    undefined,
    { equals: sameSnapshotEntries },
  );
  // forwardAuth zarr stores must re-open when the auth token rotates, so the
  // token participates in their snapshot (auth changes leave the other
  // categories untouched — only datamesh needs it, and the gateway connector
  // is already rebuilt by its own effect above).
  const zarrEntries = createMemo(
    () =>
      ofType('zarr').map((d) => ({
        id: d.id,
        snapshot: d.dataSpec?.forwardAuth
          ? `${JSON.stringify(d)}|auth:${props.authToken() ?? ''}`
          : JSON.stringify(d),
      })),
    undefined,
    { equals: sameSnapshotEntries },
  );
  // EID-132: same forwardAuth token-rotation rule as zarr — a rotated token
  // must rebuild the archive reader, since the headers are baked into its
  // range-request source.
  const pmtilesEntries = createMemo(
    () =>
      ofType('pmtiles').map((d) => ({
        id: d.id,
        snapshot: d.dataSpec?.forwardAuth
          ? `${JSON.stringify(d)}|auth:${props.authToken() ?? ''}`
          : JSON.stringify(d),
      })),
    undefined,
    { equals: sameSnapshotEntries },
  );
  const transformEntries = createMemo(
    () => ofType('transform').map(dataEntrySnapshot),
    undefined,
    { equals: sameSnapshotEntries },
  );

  // Datamesh sources: the downstream staging effect is already keyed per id
  // (it compares each query's JSON before re-staging), so refreshing the
  // array identity when an oceanql entry changes is sufficient and cheap.
  createEffect(
    on(datameshEntries, () => {
      setDatamesh(unwrap(ofType('oceanql')));
    }),
  );

  // Transforms: one watcher root per transform id. The watcher owns the
  // nested effect that waits for input data and (re)creates the worker; it is
  // created under createRoot so it survives re-runs of the diffing effect and
  // is disposed explicitly when its transform spec changes or is removed.
  // EID-126: each watcher also exposes applyCode, the warm-recompile fast
  // path for code-only spec changes.
  interface TransformWatcher {
    dispose: () => void;
    /**
     * Recompile the live worker with new code (Transform.setCode). Returns
     * false when there is no live instance yet (init in flight or inputs
     * unavailable) — the caller must fall back to the stop+start path.
     */
    applyCode: (code: string) => boolean;
  }
  const transformWatchers = new Map<string, TransformWatcher>();
  let appliedTransformSnapshots: Record<string, string> = {};

  const stopTransformWatcher = (id: string) => {
    const watcher = transformWatchers.get(id);
    if (watcher) {
      watcher.dispose();
      transformWatchers.delete(id);
    }
    // Terminate the live worker (if any) being retired.
    setTransforms((prev) => removeTransform(prev, id));
    // EID-110 (M2/R8): the registry entry above is removed, but the cached
    // {event,tx,proxy} tuple in eventTransformProxies kept a reference to the
    // retired transform (and, transitively, its input DataWrappers) alive
    // indefinitely. Drop it so the proxy cache doesn't pin memory past the
    // transform's lifetime.
    eventTransformProxies.delete(id);
    // EID-126: retire the status entry with the transform.
    setTransformStatus(
      produce((statuses) => {
        delete statuses[id];
      }),
    );
  };

  const startTransformWatcher = (transformSpec) => {
    const id = transformSpec.id;
    // EID-126: every lifecycle transition for this id lands in the status
    // store; emitted here for pre-init states and handed to Transform.init as
    // the onStatus sink for the rest.
    const onStatus = (status: TransformStatus) =>
      setTransformStatus({ [id]: status });
    // EID-125: normalize/validate here as well as in Transform.init — the
    // watcher needs the input ids (datasource values) to key its data
    // dependency, and an invalid inputs spec must refuse to start rather
    // than watch the wrong registry entries.
    let inputSpecs;
    try {
      inputSpecs = normalizeTransformInputs(
        unwrap(transformSpec.dataSpec.inputs),
      );
    } catch (e) {
      console.error(`Transform '${id}' has invalid inputs — not started:`, e);
      onStatus(
        errorStatus(
          'init',
          `Transform '${id}' has invalid inputs — not started: ${
            (e as Error)?.message ?? e
          }`,
        ),
      );
      return;
    }
    const inputIds = inputSpecs.map((input) => input.datasource);
    const outputType = transformSpec.dataSpec.outputType;
    const modules = unwrap(transformSpec.dataSpec.modules);
    // EID-126: mutable — applyCode updates it so an input-driven re-init
    // after a warm recompile initializes with the latest code.
    let code = unwrap(transformSpec.dataSpec.code);
    if (modules) {
      for (const module of Object.keys(modules)) {
        if (modules[module].startsWith('/test')) {
          modules[module] = modules[module].replace(
            '/test',
            window.location.origin + '/test',
          );
        }
      }
    }

    onStatus(initializingStatus());
    // EID-126: the live Transform instance behind the registry entry (which
    // may be a code proxy). Set when an init resolves non-stale; the warm
    // recompile fast path targets it.
    let live: Transform | null = null;

    // We need to wait for the inputs data to be available
    const dispose = createRoot((disposeRoot) => {
      // EID-104: gate on this transform's OWN input identities, not the
      // whole data registry — an unrelated datasource refresh (or another
      // transform's output landing) must not terminate and recreate this
      // transform's worker. setData always spreads the previous registry
      // ({ ...prev, [id]: wrapped }), so untouched inputs keep their
      // reference and sameInputIdentities absorbs the unrelated change.
      const ownInputs = createMemo(
        () => readOwnInputs(inputIds, data()),
        undefined,
        {
          equals: sameInputIdentities,
        },
      );
      createEffect(
        on(ownInputs, () => {
          for (const inputId of inputIds) {
            const inputdata = data()[inputId];
            if (!inputdata) {
              console.warn('Input', inputId, 'not available');
              return;
            }
          }

          // EID-57 (load-bearing): if this effect re-runs (data refresh) or
          // is disposed before the init resolves, terminate the
          // freshly-created worker instead of registering it — otherwise
          // stale workers accumulate.
          let stale = false;
          onCleanup(() => {
            stale = true;
          });

          // EID-126: the code this attempt compiles. A code-only change can
          // land while this init is in flight — applyCode updates `code` and
          // recompiles the PREVIOUS live instance (which the registration
          // below replaces and terminates) — so re-apply the newest code
          // once this instance is registered.
          const initCode = code;
          Transform.init(
            {
              id,
              code,
              inputs: inputSpecs,
              modules,
              outputType,
            },
            inputIds.map((inputId) => data()[inputId]),
            // EID-86: lazy accessor read at query time, not a reactive
            // subscription — the registry invalidation above drives re-query.
            {
              getEvent: () => currentEvent(),
              // EID-126: mute status from a superseded attempt — its late
              // error{init/import} (which 'ready' deliberately cannot clear)
              // would stick over the replacement's healthy status, and a
              // terminated instance's late emission would re-create a status
              // entry stopTransformWatcher already deleted.
              onStatus: (status) => {
                if (!stale) onStatus(status);
              },
            },
          ).then((transform) => {
            if (!transform) return;
            if (stale) {
              transform.terminate();
              return;
            }
            live = transform;
            // Terminate the transform (worker + blob URL) being replaced
            setTransforms((prev) => replaceTransform(prev, id, transform));
            // Recompile through the watcher's fast path (fresh registry
            // identity via the code proxy) so consumers that already queried
            // this instance's initCode version re-query the newest code.
            if (code !== initCode) watcher.applyCode(code);
          });
        }),
      );
      return disposeRoot;
    });

    const watcher: TransformWatcher = {
      dispose,
      applyCode: (newCode: string) => {
        // Input-driven re-inits after this point must compile the new code.
        code = newCode;
        const target = live;
        if (!target) return false;
        target.setCode(newCode).then((ok) => {
          // Compile failed (error status already reported — the previous
          // code version stays live), or the call was superseded/terminated.
          if (!ok) return;
          // Stale guards: the live instance may have been replaced by an
          // input-driven re-init, or the watcher retired, while the
          // recompile was in flight.
          if (live !== target) return;
          if (transformWatchers.get(id) !== watcher) return;
          // EID-126 cache invalidation: consumers dedupe queries by (query
          // hash, source identity) — BaseWorldLayer.cachedQuery — so the
          // recompiled transform must get a FRESH registry identity wrapping
          // the SAME live worker. Deliberately a plain spread, NOT
          // replaceTransform: its EID-57 terminate-on-replace contract would
          // kill the warm worker the proxy delegates to.
          setTransforms((prev) => ({
            ...prev,
            [id]: createTransformCodeProxy(target),
          }));
        });
        return true;
      },
    };
    transformWatchers.set(id, watcher);
  };

  createEffect(
    on(transformEntries, (entries) => {
      const prevSnapshots = appliedTransformSnapshots;
      const { changedIds, removedIds, nextById } = diffSnapshotEntries(
        prevSnapshots,
        entries,
      );
      appliedTransformSnapshots = nextById;
      for (const id of removedIds) stopTransformWatcher(id);
      if (!changedIds.length) return;
      // EID-126: changes confined to dataSpec.code recompile the LIVE worker
      // (Transform.setCode via the watcher's applyCode — no teardown, no
      // module re-import). Everything else — and code-only changes whose
      // watcher has no live instance yet — keeps the full stop+start path.
      const restartIds: string[] = [];
      for (const id of changedIds) {
        const change = classifyTransformSpecChange(
          prevSnapshots[id],
          nextById[id],
        );
        if (change.kind === 'unchanged') continue;
        if (change.kind === 'code-only') {
          const watcher = transformWatchers.get(id);
          if (watcher && watcher.applyCode(change.code)) continue;
        }
        restartIds.push(id);
      }
      if (!restartIds.length) return;
      for (const id of restartIds) stopTransformWatcher(id);
      const restart = new Set(restartIds);
      for (const transformSpec of ofType('transform')) {
        if (restart.has(transformSpec.id)) startTransformWatcher(transformSpec);
      }
    }),
  );

  //Filter and remove any data that is not in the datamesh, inline, zarr or
  //pmtiles list
  createEffect(
    on([datamesh, inline, zarr, pmtiles], () => {
      const dataIds = datamesh()
        .map((d) => d.id)
        .concat(inline().map((d) => d.id))
        .concat(zarr().map((d) => d.id))
        .concat(pmtiles().map((d) => d.id));
      setData((prev) => pruneByIds(prev, dataIds));
      //Remove any keys from refresh that are no longer in data
      setRefresh((prev) => pruneByIds(prev, dataIds));
      // EID-110 (M1): dataSpec was never pruned here, so a removed datamesh
      // source with a live-tail timefilter (query.timefilter.times[1] ===
      // null) kept matching needsRefresh() in the timer effect below forever
      // — re-staging/re-querying a source no longer in the spec on every
      // tick. Prune it alongside data/refresh.
      setDataSpec((prev) => pruneByIds(prev, dataIds));
    }),
  );

  //Create dataSpec for each datamesh data source
  createEffect(
    on([datamesh, datameshGateway], () => {
      if (!datamesh() || !datameshGateway()) return;
      const currentSpec = unwrap(dataSpec());
      datamesh().map((d) => {
        const queryStr = JSON.stringify(d.dataSpec);
        const prev = currentSpec[d.id];
        if (prev && JSON.stringify(prev.query) === queryStr) return;
        stageData(d.dataSpec).then((stage) => {
          if (!stage) return;
          // EID-110 (M2): the source was removed from the spec while this
          // stage request was in flight, so it has already been pruned from
          // the stores. Bail rather than resurrecting its dataSpec/refresh
          // entries — a re-added live-tail dataSpec would make the timer
          // effect re-query the removed source forever.
          if (!sourceStillPresent(datamesh(), d.id)) return;
          const newSpec = { query: { ...d.dataSpec }, stage };
          setRefresh((prevR) => ({
            ...prevR,
            [d.id]: prev?.stage?.qhash != stage.qhash,
          }));
          setDataSpec((prevS) => ({
            ...prevS,
            [d.id]: {
              ...newSpec,
            },
          }));
          // Eagerly load the schema into a data-less dataset so editor
          // functions can offer this source to compatible node types straight
          // after reopen, without downloading data. The data/refresh effect
          // below upgrades it with values when a view needs them; the guard
          // avoids clobbering an already-loaded wrapper.
          getSchema(stage, d.dataSpec)
            .then((dataset) => {
              if (!dataset) return;
              // EID-110 (M2): source removed while the schema load was in
              // flight — don't re-pin a data-less wrapper under the pruned id.
              if (!sourceStillPresent(datamesh(), d.id)) return;
              setData((prev) =>
                prev[d.id]
                  ? prev
                  : { ...prev, [d.id]: new DataWrapper(d.id, dataset) },
              );
            })
            .catch((e) => console.error(`Schema load failed for ${d.id}`, e));
        });
      });
    }),
  );

  //Create data entries for each data source
  createEffect(
    on([dataSpec, timer], () => {
      if (!dataSpec()) return;
      Object.keys(dataSpec()).map((did) => {
        const ds = dataSpec()[did];
        if (
          (refresh()[did] || needsRefresh(ds, timer())) &&
          !dataLoading.includes(did)
        ) {
          log('Refreshing:', did, ds.query);
          dataLoading.start(did);
          getData({ ...ds.query, id: did }, 0, ds.stage.container)
            .then((newdata) => {
              // EID-110 (M2): only re-pin data / clear the refresh flag when
              // the source is still live. A source removed while its query was
              // in flight has already been pruned; writing the stale result
              // back would resurrect its data and refresh entries. The loading
              // flag is always cleared below regardless.
              if (!!newdata && sourceStillPresent(datamesh(), did)) {
                newdata.coordkeys = ds.stage.coordkeys;
                const wrapped = new DataWrapper(did, newdata, {
                  parameters: ds.query.parameters,
                });
                setData((prev) => ({
                  ...prev,
                  [did]: wrapped,
                }));
                setRefresh((prev) => ({
                  ...prev,
                  [did]: false,
                }));
              }
              dataLoading.stop(did);
            })
            .catch((e) => {
              console.error(e);
              dataLoading.stop(did);
            });
        }
      });
    }),
  );
  // Inline datasets: only (re)load entries whose spec content changed.
  // Editing one dataset must not re-fetch the others (EID-61).
  let appliedInlineSnapshots: Record<string, string> = {};
  createEffect(
    on(inlineEntries, (entries) => {
      const { changedIds, nextById } = diffSnapshotEntries(
        appliedInlineSnapshots,
        entries,
      );
      appliedInlineSnapshots = nextById;
      const inlineSpecs = ofType('dataset', 'geojson');
      // Keep the category signal fresh for the data-pruning effect above.
      setInline(unwrap(inlineSpecs));
      if (!changedIds.length) return;
      const changed = new Set(changedIds);
      inlineSpecs
        .filter((d) => changed.has(d.id))
        .map((d) => {
          DataWrapper.inline(d.id, d.dataSpec).then((data) => {
            setData((prev) => ({
              ...prev,
              [d.id]: data,
            }));
          });
        });
    }),
  );
  // Zarr stores: only (re)open entries whose spec content changed (or whose
  // forwardAuth token rotated — the token is part of their snapshot).
  let appliedZarrSnapshots: Record<string, string> = {};
  createEffect(
    on(zarrEntries, (entries) => {
      const { changedIds, nextById } = diffSnapshotEntries(
        appliedZarrSnapshots,
        entries,
      );
      appliedZarrSnapshots = nextById;
      const zarrSpecs = ofType('zarr');
      // Keep the category signal fresh for the data-pruning effect above.
      setZarr(unwrap(zarrSpecs));
      if (!changedIds.length) return;
      const changed = new Set(changedIds);
      zarrSpecs
        .filter((d) => changed.has(d.id))
        .map((d) => {
          const id = d.id; // Capture id to avoid closure issues
          let headers = d.dataSpec.headers || {};
          if (d.dataSpec.forwardAuth && props.authToken()) {
            headers = {
              ...headers,
              Authorization: `Bearer ${props.authToken()}`,
            };
          }
          openZarrSource(
            d.dataSpec.group,
            headers,
            d.dataSpec.coordkeys,
            d.dataSpec.ttl,
          ).then(({ dataset, multiscale }) => {
            const available = new Set([
              ...Object.keys(dataset.dimensions || {}),
              ...Object.keys(dataset.variables || {}),
            ]);
            for (const [key, name] of Object.entries(
              d.dataSpec.coordkeys || {},
            )) {
              if (name && !available.has(name as string)) {
                console.warn(
                  `Datasource "${id}" coordkey ${key}="${name}" not found in dataset (available: ${[...available].join(', ')})`,
                );
              }
            }
            const wrapped = new DataWrapper(id, dataset);
            // EID-131: only present on multiscale stores, so a flat store's
            // wrapper keeps exactly the shape (and JSON) it has today.
            if (multiscale) wrapped.multiscale = multiscale;
            setData((prev) => ({
              ...prev,
              [id]: wrapped,
            }));
          });
        });
    }),
  );
  // PMTiles archives (EID-132): only (re)open entries whose spec content
  // changed (or whose forwardAuth token rotated). Unlike every other category
  // this registers no Dataset — tiles are the vector LOD unit, so the registry
  // entry is a wrapper carrying a tile-access handle (getTile + archive
  // header/bounds/zoom metadata). Opening is synchronous and cheap: the
  // archive header is only read when the first tile is requested.
  let appliedPMTilesSnapshots: Record<string, string> = {};
  createEffect(
    on(pmtilesEntries, (entries) => {
      const { changedIds, nextById } = diffSnapshotEntries(
        appliedPMTilesSnapshots,
        entries,
      );
      appliedPMTilesSnapshots = nextById;
      const pmtilesSpecs = ofType('pmtiles');
      // Keep the category signal fresh for the data-pruning effect above.
      setPmtiles(unwrap(pmtilesSpecs));
      if (!changedIds.length) return;
      const changed = new Set(changedIds);
      for (const d of pmtilesSpecs) {
        if (!changed.has(d.id)) continue;
        const headers = pmtilesRequestHeaders(d.dataSpec, props.authToken());
        const wrapped = new DataWrapper(d.id, null);
        // Only present on pmtiles sources, so every other wrapper keeps
        // exactly the shape it has today; DataWrapper.query() returns null
        // without a datasource, so a layer that does not know about tiles
        // simply renders nothing rather than misreading the entry.
        wrapped.pmtiles = openPMTiles({ url: d.dataSpec.url, headers });
        setData((prev) => ({
          ...prev,
          [d.id]: wrapped,
        }));
      }
    }),
  );

  // Dispose all transform watcher roots (created via createRoot, so not
  // owned by this component) and terminate all workers when the provider
  // unmounts.
  onCleanup(() => {
    for (const watcher of transformWatchers.values()) watcher.dispose();
    transformWatchers.clear();
    terminateAll(transforms());
  });

  // EID-137: LOD level switching for multiscale (GeoZarr) datasources.
  //
  // The camera lives in the deckmap and the registry lives here, so the deckmap
  // hands us a resolution demand and this function decides whether it implies a
  // different pyramid level — and only then reopens. Keeping the decision here
  // means the "same level ⇒ no refetch" guarantee is enforced in one place
  // rather than trusted to every caller.
  //
  // `activeScale` on the wrapper is the memo of what is currently open. It
  // starts undefined (openWithScales opened the coarsest level as its fallback),
  // so the first demand always resolves and refines.
  const scaleRequests = new Map<string, number>();
  const requestScale = (
    dataId: string,
    metresPerPixel: number,
    latitude = 0,
  ) => {
    const wrapper = data()[dataId];
    const multiscale = wrapper?.multiscale;
    if (!multiscale?.scales?.length) return; // flat datasource: unchanged path
    // planScaleChange owns the unit conversion (a level's cellSize is in ITS
    // OWN CRS units — degrees for a geographic pyramid, metres for a projected
    // one) and the "is this actually a change" test. Both are pure and tested
    // directly in lod.test.ts.
    const { target, changed } = planScaleChange({
      scales: multiscale.scales,
      activeScale: wrapper.activeScale,
      metresPerPixel,
      latitude,
    });
    if (!target || !changed) return; // already showing it: no refetch
    if (scaleRequests.get(dataId) === target.level) return; // already in flight
    scaleRequests.set(dataId, target.level);
    // EID-138 best-available rendering: the wrapper is replaced ONLY inside
    // .then(). The current level therefore stays mounted and rendering for the
    // whole flight of the request — the view degrades to "slightly stale" and
    // never to "blank, then pop". Moving setData outside the promise, or
    // clearing the entry before requesting, would reintroduce exactly the
    // artefact this is meant to avoid.
    multiscale
      .openScale(target.level)
      .then((dataset) => {
        // A newer request overtook this one while it was in flight — drop the
        // stale result rather than letting it clobber a finer level.
        if (scaleRequests.get(dataId) !== target.level) return;
        scaleRequests.delete(dataId);
        setData((prev) => {
          const current = prev[dataId];
          if (!current) return prev; // datasource pruned mid-flight
          const next = new DataWrapper(dataId, dataset, current.oceanql);
          next.multiscale = multiscale;
          next.activeScale = target.level;
          next.scaleLevel = target;
          return { ...prev, [dataId]: next };
        });
      })
      .catch((e) => {
        scaleRequests.delete(dataId);
        console.warn(
          `LOD: could not open level ${target.level} of datasource "${dataId}" — staying on the current level`,
          e,
        );
      });
  };

  return (
    <DataContext.Provider
      value={{
        dataSpec,
        dataRegistry,
        // EID-137: the deckmap calls this with a resolution demand; see above.
        requestScale,
        // EID-126: per-transform lifecycle status store (id → TransformStatus).
        transformStatus,
      }}
    >
      {props.children}
    </DataContext.Provider>
  );
};

export { useData, DataProvider, useDataLoading, dataLoading };
